/*
 * ******************************************************************
 * Copyright (c) 2024-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, QueryList, ViewChildren } from "@angular/core";
import { ClrDatagridFilter, ClrDatagridFilterInterface, ClrPopoverToggleService } from "@clr/angular";
import { Observable, Subject } from "rxjs";
import { L10nService } from "@vmw/ngx-vip";
import { EnumDescriptor } from "@shared/models/server-driven-filter.model";
import { takeUntil } from "rxjs/operators";

export class EnumItemModel {
    constructor(public label: string,
                public value: string,
                public selected: boolean = false) {
    }
}

// @formatter:off
@Component({
    standalone: false,
    selector: "dg-checkbox-filter",
    templateUrl: "./datagrid-checkbox-filter.component.html",
    styleUrls: ["./datagrid-checkbox-filter.component.scss"]
})
// @formatter:on
export class DatagridCheckboxFilter<T> implements ClrDatagridFilterInterface<T>, AfterViewInit, OnDestroy {
    @ViewChildren('input', { read: ElementRef })
    private inputRef: QueryList<ElementRef>;

    @Input()
    public filterProperty: string;

    /**
     * Resource strings for enum values used to show in the filter.
     * @param value - array of strings
     */
    @Input()
    public set enumDescriptors(value: EnumDescriptor[]) {
        if (value && value.length > 0) {
            this._enumDescriptors = value;
            this.selectedItems = [];
            this.initFilter();
        }
    }

    public get enumDescriptors(): EnumDescriptor[] {
        return this._enumDescriptors;
    }

    /**
     * Preset filter selection.
     * @param value - array of strings
     */
    @Input()
    public set clrFilterValue(value: string[]) {
        this._initialValue = value;
        this.presetFilter();
    }

    public get clrFilterValue(): string[] {
        return this.selectedItems.map(item => item.value);
    }

    @Output()
    clrFilterValueChange: EventEmitter<string[]> = new EventEmitter();

    public items: Array<EnumItemModel>;
    public selectedItems: Array<EnumItemModel> = new Array<EnumItemModel>();
    public buttonText: string;
    public allSelected: boolean = false;
    private _enumDescriptors: EnumDescriptor[];
    private _initialValue: string[];
    private changesSubject: Subject<EnumItemModel[]> = new Subject();
    private destroySubject: Subject<void> = new Subject<void>();

    constructor(protected filterContainer: ClrDatagridFilter,
                private smartToggleService: ClrPopoverToggleService,
                private ngZone: NgZone,
                protected l10nService: L10nService) {
        filterContainer.setFilter(this);
    }

    ngAfterViewInit(): void {
        this.smartToggleService.openChange
            .pipe(takeUntil(this.destroySubject))
            .subscribe(() => {
                this.ngZone.runOutsideAngular(() => {
                    requestAnimationFrame(() => {
                        if (this.inputRef.length > 0) {
                            this.inputRef.first.nativeElement.focus();
                        }
                    });
                });
            });
    }

    ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }


    /**
     * Indicates if the filter is currently active, (at least one input is set)
     */
    public isActive(): boolean {
        return this.selectedItems.length > 0;
    }

    /**
     * The method is not used for server-driven datagrids, so no need to implement it.
     */
    public accepts(unused: T): boolean {
        return false;
    }

    public get state(): any {
        return {
            property: this.filterProperty,
            value: this.selectedItems.map(item => item.value)
        }
    }

    /**
     * The Observable required as part of the Filter interface
     */
    @Output()
    public get changes(): Observable<EnumItemModel[]> {
        return this.changesSubject.asObservable();
    }

    public onCheckboxChecked(): void {
        this.selectedItems = this.items.filter(item => item.selected === true);
        this.checkSelectedItems();
        this.changesSubject.next(this.selectedItems);
        this.clrFilterValueChange.emit(this.selectedItems.map(item => item.value));
    }

    checkSelectedItems(): void {
        let allSelected: boolean = true;
        for (const item of this.items) {
            if (item.value !== "EMPTY") {
                allSelected = allSelected && item.selected;
            }
        }
        this.allSelected = allSelected;
        this.updateButtonText();
    }

    updateButtonText(): void {
        this.buttonText = this.allSelected ?
            "filter.unselectAll.button.text" :
            "filter.selectAll.button.text";
    }

    onButtonClick(): void {
        this.allSelected = !this.allSelected;

        this.items.forEach(item => item.selected = this.allSelected);
        this.selectedItems = this.items.filter(item => item.selected === true);
        this.updateButtonText();

        this.changesSubject.next(this.selectedItems);
        this.clrFilterValueChange.emit(this.selectedItems.map(item => item.value));
    }

    private presetFilter(): void {
        if (!this._initialValue) {
            return;
        }
        if (this.items) {
            for (const item of this.items) {
                if (this._initialValue.includes(item.value) &&
                    !this.selectedItems.includes(item)) {
                    item.selected = true;
                    this.selectedItems.push(item);
                }
            }
        }
        this.changesSubject.next(this.selectedItems);
    }

    private initFilter(): void {
        this.items = new Array<EnumItemModel>();
        if (this._enumDescriptors) {
            for (const item of this._enumDescriptors) {
                let str: string;
                if (item.labelResourceId) {
                    str = item.labelResourceId === "EMPTY" ? "" : this.l10nService.getMessage(item.labelResourceId);
                } else {
                    str = item.labelResourceValue;
                }
                this.items.push(new EnumItemModel(str, item.enumValue));
            }
        }

        this.updateButtonText();
        this.presetFilter();
    }
}
